import { useState, useCallback } from 'react';
import { Copy, Mail, Check, X, PartyPopper, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCurrentTheme, withLangAndTheme } from '@/utils/siteSync';

interface NewRecordModalProps {
  score: number;
  couponCode: string;
  onClose: () => void;
}

type ModalPhase = 'coupon' | 'services';

export function NewRecordModal({ score, couponCode, onClose }: NewRecordModalProps) {
  const { t, language } = useLanguage();
  const m = t.modal;
  const theme = useCurrentTheme();
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [phase, setPhase] = useState<ModalPhase>('coupon');

  const transitionToServices = useCallback(() => {
    setTimeout(() => setPhase('services'), 2000);
  }, []);

  const copyCode = async () => {
    await navigator.clipboard.writeText(couponCode);
    setCopied(true);
    transitionToServices();
  };

  const sendEmail = async () => {
    if (!email || !email.includes('@') || !marketingConsent) return;
    setSending(true);
    try {
      await supabase.functions.invoke('send-coupon-email', {
        body: { couponCode, email, marketingConsent, language, score },
      });
      setSent(true);
      transitionToServices();
    } catch (e) {
      console.error('Email send failed:', e);
    }
    setSending(false);
  };

  if (phase === 'services') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
        <div className="glass-card max-w-md w-full p-8 relative glow-effect animate-in fade-in zoom-in-95 duration-300">
          <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>

          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold gradient-text">{m.useDiscount}</h2>
            <p className="text-sm text-muted-foreground mt-2">
              {m.useDiscountDesc} <strong className="text-foreground">{couponCode}</strong> {m.useDiscountSuffix}
            </p>
          </div>

          {/* Erikoiskampanja */}
          <div className="rounded-xl p-4 mb-4 border border-accent/30" style={{ background: 'linear-gradient(135deg, hsl(var(--haiko-blue) / 0.15), hsl(var(--haiko-purple) / 0.15))' }}>
            <p className="text-xs font-bold text-accent uppercase tracking-wider mb-1">{m.specialOfferTitle}</p>
            <p className="text-sm font-bold text-foreground">{m.specialOfferPrice} <span className="text-muted-foreground font-normal">· {m.specialOfferMaintenance}</span></p>
            <p className="text-xs text-muted-foreground mt-1">{m.specialOfferDesc}</p>
            <a
              href={withLangAndTheme('https://haikoinnovations.com/campaign', language, theme)}
              className="inline-block mt-2 text-xs font-semibold text-accent hover:underline"
            >
              {m.readMore}
            </a>
          </div>

          <div className="space-y-3 mb-6">
            {[
              { icon: '💡', label: m.serviceCore, desc: m.serviceCoreDesc },
              { icon: '⚙️', label: m.serviceFlow, desc: m.serviceFlowDesc },
              { icon: '🔒', label: m.serviceTrust, desc: m.serviceTrustDesc },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-3 rounded-lg bg-secondary/50 px-4 py-2.5">
                <span className="text-lg">{s.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{s.label}</p>
                  <p className="text-xs text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <a
            href={withLangAndTheme('https://haikoinnovations.com/#contact', language, theme)}
            className="w-full btn-gradient flex items-center justify-center gap-2"
          >
            {m.contactUs} <ExternalLink className="w-4 h-4" />
          </a>

          <a
            href={withLangAndTheme('https://haikoinnovations.com/#services', language, theme)}
            className="block text-center text-sm text-accent mt-3 hover:underline"
          >
            {m.exploreServices}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="glass-card max-w-md w-full p-8 relative glow-effect animate-in fade-in zoom-in-95 duration-300">
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <PartyPopper className="w-12 h-12 mx-auto mb-3 text-accent" />
          <h2 className="text-2xl font-bold gradient-text">{m.newRecord}</h2>
          <p className="text-4xl font-extrabold text-foreground mt-2">{score.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground mt-1">{m.points}</p>
        </div>

        <div className="rounded-xl p-4 mb-6" style={{ background: 'linear-gradient(135deg, hsl(var(--haiko-blue)), hsl(var(--haiko-purple)))' }}>
          <p className="text-primary-foreground/70 text-xs uppercase tracking-wider mb-1">{m.discountCode}</p>
          <p className="text-2xl font-bold text-primary-foreground tracking-widest text-center">{couponCode}</p>
        </div>

        <p className="text-sm text-muted-foreground text-center mb-3">
          {m.copyOrEmail}
        </p>

        <button
          onClick={copyCode}
          className="w-full btn-gradient flex items-center justify-center gap-2 mb-4"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? m.copied : m.copy}
        </button>

        {!sent ? (
          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={e => setMarketingConsent(e.target.checked)}
                className="mt-0.5 rounded border-border accent-accent"
              />
              <span className="text-xs text-muted-foreground leading-tight">
                {m.marketingConsent}{' '}
                <a
                  href={withLangAndTheme('https://haikoinnovations.com/privacy', language, theme)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline hover:text-accent/80"
                >
                  {m.privacyPolicy}
                </a>
              </span>
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder={m.emailPlaceholder}
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="flex-1 rounded-xl bg-secondary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground border border-border focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <button
                onClick={sendEmail}
                disabled={sending || !email.includes('@') || !marketingConsent}
                className="rounded-xl bg-accent px-4 py-3 text-accent-foreground font-semibold disabled:opacity-50 transition-colors hover:bg-accent/80"
                title={m.sendEmail}
              >
                <Mail className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <p className="text-center text-sm text-accent font-medium">
            ✅ {m.emailSent} {email}
          </p>
        )}

        <p className="text-xs text-muted-foreground mt-4 text-center">
          {m.validDays}
        </p>
      </div>
    </div>
  );
}
